import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography
} from '@mui/material';

const EditRoleDialog = ({ open, onClose, onSubmit, user }) => {
    const [formData, setFormData] = useState({
        director_id: '',
        state_ids: '',
        sport_ids: '',
        notes: '',
        email: '',
        first_name: '',
        last_name: '',
        nickname: '',
        phone_number: ''
    });

    useEffect(() => {
        if (user) {
            const roleData = user.metadata?.role_data || {};
            setFormData({
                director_id: roleData.director_id || '',
                state_ids: roleData.state_ids ? roleData.state_ids.join(', ') : '',
                sport_ids: roleData.sport_ids ? roleData.sport_ids.join(', ') : '',
                notes: roleData.notes || '',
                email: user.email || '',
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                nickname: user.nickname || '',
                phone_number: user.phone_number || ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const isStaff = user?.role === 'staff';

    const handleSubmit = () => {
        const payload = {
            user_id: user.id,
            director_id: formData.director_id ? parseInt(formData.director_id) : undefined,
            state_ids: formData.state_ids ? formData.state_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [],
            sport_ids: formData.sport_ids ? formData.sport_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [],
            notes: formData.notes
        };

        // Only include personal info if the user is staff
        if (isStaff) {
            payload.email = formData.email;
            payload.first_name = formData.first_name;
            payload.last_name = formData.last_name;
            payload.nickname = formData.nickname;
            payload.phone_number = formData.phone_number;
        }

        onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Role: {user?.first_name} {user?.last_name}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                        Role: {user?.role}
                    </Typography>

                    {(user?.role === 'director' || user?.role === 'state_director') && (
                        <TextField
                            label="Director ID"
                            name="director_id"
                            value={formData.director_id}
                            onChange={handleChange}
                            fullWidth
                        />
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="First Name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            fullWidth
                            disabled={!isStaff}
                            helperText={!isStaff ? "Only editable for staff" : ""}
                        />
                        <TextField
                            label="Last Name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            fullWidth
                            disabled={!isStaff}
                            helperText={!isStaff ? "Only editable for staff" : ""}
                        />
                    </Box>

                    <TextField
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        fullWidth
                        disabled={!isStaff}
                        helperText={!isStaff ? "Only editable for staff" : ""}
                    />

                    <TextField
                        label="Phone Number"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleChange}
                        fullWidth
                        disabled={!isStaff}
                        helperText={!isStaff ? "Only editable for staff" : ""}
                    />

                    <TextField
                        label="Nickname"
                        name="nickname"
                        value={formData.nickname}
                        onChange={handleChange}
                        fullWidth
                        disabled={!isStaff}
                        helperText={!isStaff ? "Only editable for staff" : ""}
                    />

                    <TextField
                        label="State IDs (comma separated)"
                        name="state_ids"
                        value={formData.state_ids}
                        onChange={handleChange}
                        fullWidth
                        placeholder="e.g. 1, 12, 45"
                    />

                    <TextField
                        label="Sport IDs (comma separated)"
                        name="sport_ids"
                        value={formData.sport_ids}
                        onChange={handleChange}
                        fullWidth
                        placeholder="e.g. 11, 17"
                    />

                    <TextField
                        label="Notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={3}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">Save Changes</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRoleDialog;
